import { ApiError, req } from "../api.ts";
import { usePlaySession } from "../session.ts";
import { clientInstance, socketLimitMessage } from "../clientInstance.ts";
import { showSocketNotice } from "../ui/serverStatus.ts";

export interface Player { id: string; username: string; kind: "guest" | "account"; account_id: string | null; expires_at: number }
export interface Seat { id: string; name: string }
export interface Slot {
  id: string; player_id: string; revision: number; state: "waiting" | "paired" | "active" | "cancelled" | "expired";
  kind: "fresh" | "import" | "match"; host: Seat; recipient: Seat | null;
  white_id: string | null; black_id: string | null; expires_at: number; server_time: number;
  snapshot_digest: string; approved: boolean; invite_url?: string; game_id: string | null;
  readiness: { host: boolean; recipient: boolean };
  setup?: { kind: string; color?: "white" | "black"; transfer?: { exporter?: {build: string}; snapshot: { final_fen: string }; source: {initiator_color: "white" | "black"; match_id?: string} } };
  terms: Record<string, unknown>;
}
export interface Result { command_id: string; outcome: "applied" | "rejected"; code?: string; slot?: Slot; seek?: Seek }
export interface Seek { id: string; player_id: string; name: string; revision: number; state: string; slot_id: string | null; expires_at: number }
export const commandId = () => `${Math.floor(Date.now()/1000)}_${crypto.randomUUID().replaceAll("-", "")}`;
let player: Player | null = null;
let establishing: Promise<Player> | undefined;
export function currentPlayer(): Player | null { return player; }
export async function readPlayer(): Promise<Player | null> {
  usePlaySession();
  try { player = (await req<{player: Player}>("GET", "/api/play/session")).player; }
  catch (err) { if (!(err instanceof ApiError) || err.status !== 401) throw err; player = null; }
  return player;
}

// Web Locks serialize tabs, including both bootstrap responses. Older Safari
// uses an IndexedDB transaction to acquire a short lease on the same operation.
async function browserLock<T>(run: () => Promise<T>): Promise<T> {
  if (navigator.locks) return navigator.locks.request("chessme.guest-bootstrap.v1", run);
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open("chessme.bootstrap-lock.v1", 1);
    open.onupgradeneeded = () => open.result.createObjectStore("lease");
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(new Error("Allow browser storage to start a private game."));
  });
  const owner = crypto.randomUUID();
  async function lease(release = false): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction("lease", "readwrite"); const store = tx.objectStore("lease");
      const read = store.get("bootstrap"); let acquired = false;
      read.onsuccess = () => {
        const old = read.result as { owner: string; until: number } | undefined;
        if (release) { if (old?.owner === owner) store.delete("bootstrap"); }
        else if (!old || old.until < Date.now() || old.owner === owner) {
          store.put({owner, until: Date.now()+90_000}, "bootstrap"); acquired = true;
        }
      };
      tx.oncomplete = () => resolve(acquired);
      tx.onerror = tx.onabort = () => reject(new Error("Browser storage is unavailable. Please reload."));
    });
  }
  try {
    const deadline = Date.now()+95_000;
    while (!await lease()) {
      if (Date.now() > deadline) throw new Error("Another tab is starting your session. Please retry.");
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return await run();
  } finally { await lease(true); db.close(); }
}
export function ensurePlayer(): Promise<Player> {
  usePlaySession();
  if (!establishing) establishing = browserLock(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const response = await req<{player?: Player; pending?: boolean}>("POST", "/api/play/session");
      if (response.player) {
        const current = await readPlayer();
        if (current && current.id === response.player.id) return current;
        throw new Error("Your browser session changed. Reload before continuing.");
      }
    }
    throw new Error("Allow cookies to keep your private game in this browser.");
  }).finally(() => { establishing = undefined; });
  return establishing;
}

/** An expired cookie needs an explicit new-guest action, never a silent new seat. */
export function sessionRecovery(root: HTMLElement, error: unknown): void {
  if (error instanceof PendingPlayAction) {
    root.querySelector('[data-pending-recovery]')?.remove();
    const panel = document.createElement('div'); panel.dataset.pendingRecovery = '';
    const note = document.createElement('p'); note.textContent = error.message;
    const retry = document.createElement('button'); retry.className = 'btn'; retry.textContent = 'Check previous action';
    retry.onclick = async () => {
      retry.disabled = true;
      try { const result = await error.recover();
        note.textContent = result.outcome === 'rejected' ? outcomeMessage(result.code) : 'Your previous action was confirmed. Reload to review the result.';
        retry.textContent = 'Reload to review'; retry.onclick = () => location.reload();
      } catch (failure) { note.textContent = failure instanceof Error ? failure.message : 'Please retry.'; }
      finally { retry.disabled = false; }
    };
    panel.append(note, retry); root.append(panel); return;
  }
  if (!(error instanceof ApiError) || error.status !== 401 || root.querySelector('[data-session-recovery]')) return;
  const panel=document.createElement('div'); panel.dataset.sessionRecovery='';
  const explanation=document.createElement('p');
  explanation.textContent='This browser session has expired. A new guest cannot recover unsaved seats from the old session. Games saved by email remain recoverable from My games.';
  const button=document.createElement('button');button.className='btn';button.textContent='Start a new guest';
  button.onclick=async()=> {
    button.disabled=true;
    try {
      await browserLock(()=>req('POST','/api/play/session/reset'));
      location.reload();
    } catch(error) {explanation.textContent=error instanceof Error?error.message:'Please retry.';button.disabled=false;}
  };
  panel.append(explanation,button);root.append(panel);
}

let renewedAt=0;
/** Explicit play can renew a guest; idle sockets never keep the game alive. */
export async function renewGuestOnActivity():Promise<void> {
  if(!player||player.kind!=='guest'||Date.now()-renewedAt<3_600_000||player.expires_at*1000>Date.now()+5*86400_000)return;
  const actor=player.id;
  const current=await readPlayer();
  if(!current||current.id!==actor)throw new Error('Your browser session changed. Save your move result, then reload.');
  const response=await req<{player?:Player}>('POST','/api/play/session');
  if(!response.player||response.player.id!==actor)throw new Error('Your browser session changed. Reload to continue.');
  player=response.player;renewedAt=Date.now();
}

/** Save before sending; an unknown outcome always retries the original body. */
export class PendingPlayAction extends Error {
  constructor(public recover: () => Promise<Result>) {
    super("Your earlier action has an unknown result. Check it before choosing a different action.");
  }
}
export class PlayJournal {
  private key: string;
  constructor(player: string, scope: string) { this.key = `chessme.play.v1:${player}:${scope}`; }
  async send(path: string, body: Record<string, unknown>, committed?: (result: Result) => void): Promise<Result> {
    const old = sessionStorage.getItem(this.key);
    const request = old ? JSON.parse(old) as {path: string; body: unknown} : {path, body};
    const intent = (value: unknown) => {
      const { command_id: _, ...rest } = value as Record<string, unknown>;
      return JSON.stringify(rest);
    };
    if (old && (request.path !== path || intent(request.body) !== intent(body))) {
      throw new PendingPlayAction(() => this.send(request.path, request.body as Record<string, unknown>, committed));
    }
    sessionStorage.setItem(this.key, JSON.stringify(request));
    const result = await req<Result>("POST", request.path, request.body);
    if (result.command_id !== (request.body as {command_id: string}).command_id) throw new Error("Checking your previous request. Please retry.");
    committed?.(result);
    sessionStorage.removeItem(this.key);
    return result;
  }
}

class PlayConnectionTransport {
  id: string | null = null;
  error = "Connecting…";
  private ws?: WebSocket;
  private stopped = false;
  private retry?: number;
  private watch?: number;
  private until = 0;
  private attempts = 0;
  private clearNotice?: () => void;
  private visibility = () => {
    this.id = null; this.until = 0;
    if (document.visibilityState !== "visible") this.send({t:"hidden"});
    else this.ws?.close(); // A new foreground connection gets a new challenge.
    this.changed();
  };
  constructor(private changed: () => void, private event: (event: {t:string; d:unknown}) => void = () => {}) {}
  start(): void {
    document.addEventListener("visibilitychange", this.visibility);
    this.watch = window.setInterval(() => {
      if (this.id && Date.now() >= this.until) { this.id = null; this.error = "Reconnecting…"; this.changed(); this.ws?.close(); }
    }, 1000);
    this.connect();
  }
  private connect(): void {
    if (this.stopped) return;
    clearTimeout(this.retry); this.clearNotice?.(); this.clearNotice = undefined;
    const ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws/play?client_id=${clientInstance()}`);
    this.ws = ws;
    ws.onmessage = e => {
      if (ws !== this.ws || this.stopped) return;
      let message: {t:string; d:any};
      try { message = JSON.parse(String(e.data)); } catch { return; }
      if (message.t === "hello" || message.t === "challenge") {
        this.send({t:"ack", d:{challenge:message.d.challenge, foreground:document.visibilityState === "visible"}});
      } else if (message.t === "ack") {
        this.id = message.d.foreground && document.visibilityState === "visible" ? message.d.connection_id : null;
        this.until = this.id ? Date.now()+25_000 : 0; this.error = this.id ? "Connected" : "Waiting for this browser";
        this.attempts = 0; this.changed();
      } else this.event(message);
    };
    ws.onclose = e => {
      if (ws !== this.ws || this.stopped) return;
      this.id = null;
      const stop = socketLimitMessage(e.code); this.error = stop ?? "Reconnecting…";
      this.changed();
      if (stop) this.clearNotice = showSocketNotice(stop, () => this.connect());
      if (!stop) this.retry = window.setTimeout(() => this.connect(), Math.min(8000,500*2**this.attempts++));
    };
    ws.onerror = () => ws.close();
  }
  private send(value: unknown): void { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(value)); }
  close(): void {
    this.stopped = true; this.id = null;
    this.clearNotice?.(); this.clearNotice = undefined;
    document.removeEventListener("visibilitychange", this.visibility);
    clearTimeout(this.retry); clearInterval(this.watch); this.ws?.close();
  }
}

// Keep the exact reservation connection across SPA screen changes. A seek's
// lease cannot be replaced by a new socket when its waiting room opens.
let transport: PlayConnectionTransport | undefined;
let transportClose: number | undefined;
const subscribers = new Set<PlayConnection>();
export class PlayConnection {
  private started = false;
  constructor(public changed: () => void, public event: (event: {t:string;d:unknown}) => void = () => {}) {}
  get id(): string | null { return transport?.id ?? null; }
  get error(): string { return transport?.error ?? "Connecting…"; }
  start(): void {
    if (this.started) return;
    this.started = true; clearTimeout(transportClose); subscribers.add(this);
    if (!transport) {
      transport = new PlayConnectionTransport(() => { for (const s of subscribers) s.changed(); },event => { for (const s of subscribers) s.event(event); });
      transport.start();
    } else this.changed();
  }
  close(): void {
    if (!this.started) return;
    this.started = false; subscribers.delete(this);
    if (!subscribers.size) transportClose = window.setTimeout(() => {
      if (!subscribers.size) { transport?.close(); transport = undefined; }
    },2000);
  }
}

export const refusalMessages: Record<string,string> = {
  capacity_full:"The club is full for now. Please try again shortly.",
  player_busy:"One of you already has a live game or a match reserved.",
  fresh_ack_required:"Both browsers need a fresh connection check. Wait for Connected, then select I’m ready again.",
  stale_revision:"The invitation changed. Review the current players, position and sides, then try again.",
  terms_changed:"The players, position or sides changed. Review them before confirming again.",
  source_conflict:"This position already has a different live continuation.",
  source_finished:"That continuation has finished. Open it in My games or start a new invitation.",
  admissions_closed:"New games are temporarily paused. Try again later.",
  expired:"This invitation expired. Create a new invitation to play.",
  cancelled:"This invitation was cancelled. Create a new invitation to play.",
  not_connected:"Your browser is not connected. Wait for Connected, then try again.",
  self_claim:"This is your own invitation. Open your waiting room to share it with a friend.",
  already_claimed:"Someone already joined. Return to the original browser or ask the host for a new invitation.",
  approval_required:"The host must confirm the player and position before you can be ready.",
  expired_command:"This saved request expired. Start the action again.",
  command_conflict:"This saved request does not match its original action. Reload to review the current invitation.",
  invalid_players:"Two different players are required. Ask your friend to use their own browser session.",
  forbidden:"These players cannot start this game. Review the invitation or choose another player.",
  unavailable:"That player is no longer available. Choose another player or start a new search.",
};
export function outcomeMessage(code?: string): string {
  return refusalMessages[code ?? ""] ?? "The game could not start. Review the invitation and try again.";
}
